/**
 * ComprobanteBlock — Dropzone reutilizable para subir evidencia de pago con
 * análisis Vision automático.
 *
 * Sube el archivo a /turismo/soportes/upload (devuelve URL pública), luego
 * llama a /turismo/soportes/analizar-comprobante para extraer monto, cuenta
 * destino, referencia. Los campos detectados son sugerencias — el padre
 * decide qué usar (pre-pobla campos vacíos, no sobrescribe).
 *
 * Se usa desde AdminCxC (aplicar pago) y AdminComisiones (liquidar pago).
 * El componente NO llama al backend directamente — recibe callbacks `onFile`
 * y `onQuitar` para que el padre maneje upload+análisis y el estado.
 *
 * Props:
 *   url             {string}  URL del archivo subido (vacía si aún no)
 *   nombre          {string}  Nombre original del archivo
 *   subiendo        {bool}    True mientras se sube
 *   analizando      {bool}    True mientras Vision corre
 *   analisis        {object}  Respuesta de /soportes/analizar-comprobante
 *   error           {string}  Mensaje de error a mostrar
 *   cuentaDestinoId {any}     Cuenta actualmente seleccionada (para chip ✓)
 *   onFile          {func}    Callback (File) → padre hace upload+análisis
 *   onQuitar        {func}    Callback → padre limpia el estado
 *   discrepancia    {object}  {detectado, distribuido, diff, pct} opcional
 *   fmtMoney        {func}    Formateador de moneda a usar
 *   titulo          {string}  Título de la sección (default "Evidencia del pago")
 */
import React, { useState, useRef } from 'react';
import { Box, Typography, Chip, Button, Alert, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DoneIcon from '@mui/icons-material/Done';

export default function ComprobanteBlock({
  url, nombre, subiendo, analizando, analisis, error,
  cuentaDestinoId, onFile, onQuitar, discrepancia,
  fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`,
  titulo = 'Evidencia del pago (opcional)',
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onFile(f);
  };

  const cuentaSugerida = analisis?.cuenta_destino_match?.cuenta_id;
  const cuentaSugeridaNombre = analisis?.cuenta_destino_match?.cuenta_nombre;
  const scoreCuenta = analisis?.cuenta_destino_match?.score || 0;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{
        fontSize: 12, fontWeight: 700, color: '#0891b2',
        textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5,
      }}>
        {titulo}
      </Typography>

      {!url ? (
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: `2px dashed ${dragOver ? '#0891b2' : '#cbd5e1'}`,
            bgcolor: dragOver ? '#f0f9ff' : '#fafafa',
            borderRadius: 2, p: 2, textAlign: 'center', cursor: 'pointer',
            transition: 'all 0.15s',
            '&:hover': { borderColor: '#0891b2', bgcolor: '#f0f9ff' },
          }}
        >
          <input ref={inputRef} type="file" hidden
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(e) => onFile(e.target.files?.[0])} />
          <CloudUploadIcon sx={{ fontSize: 32, color: '#0891b2', mb: 0.5 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
            Sube el comprobante (voucher, transferencia, tirilla)
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.25 }}>
            Arrastra la imagen o haz click · JPG, PNG, WebP, PDF · máx 10 MB
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: '#0891b2', mt: 0.5, fontStyle: 'italic' }}>
            <AutoAwesomeIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />
            Claude Vision lee el monto y la cuenta destino automáticamente
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          border: '1px solid #cbd5e1', borderRadius: 2, p: 1.25,
          bgcolor: '#f8fafc',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: analisis ? 1 : 0 }}>
            <DoneIcon sx={{ fontSize: 18, color: '#15803d' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a', flex: 1, minWidth: 0 }} noWrap>
              {nombre}
            </Typography>
            <Button size="small" href={url} target="_blank" rel="noopener"
              sx={{ textTransform: 'none', fontSize: 11 }}>
              Ver
            </Button>
            <Button size="small" onClick={onQuitar}
              sx={{ textTransform: 'none', fontSize: 11, color: '#dc2626' }}>
              Quitar
            </Button>
          </Box>

          {(subiendo || analizando) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                {subiendo ? 'Subiendo archivo…' : 'Analizando con Claude Vision…'}
              </Typography>
            </Box>
          )}

          {analisis && !analizando && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {analisis.monto != null && (
                <Chip size="small" label={`Monto: ${fmtMoney(analisis.monto)}`}
                  sx={{ bgcolor: '#ecfdf5', color: '#166534', fontWeight: 700, fontSize: 11 }} />
              )}
              {analisis.referencia && (
                <Chip size="small" label={`Ref: ${analisis.referencia}`}
                  sx={{ bgcolor: '#f0f9ff', color: '#0369a1', fontWeight: 600, fontSize: 11 }} />
              )}
              {cuentaSugerida ? (
                <Chip size="small"
                  label={
                    cuentaDestinoId === cuentaSugerida
                      ? `✓ Cuenta detectada: ${cuentaSugeridaNombre}`
                      : `Cuenta detectada: ${cuentaSugeridaNombre} (score ${scoreCuenta})`
                  }
                  sx={{ bgcolor: '#faf5ff', color: '#6b21a8', fontWeight: 600, fontSize: 11 }} />
              ) : analisis?.cuenta_destino_texto ? (
                <Chip size="small"
                  label={`Cuenta leída: "${analisis.cuenta_destino_texto}" (sin match — selecciona manual)`}
                  sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: 11 }} />
              ) : null}
              {analisis.confianza != null && (
                <Chip size="small"
                  label={`Confianza ${(analisis.confianza * 100).toFixed(0)}%`}
                  sx={{
                    bgcolor: analisis.confianza >= 0.85 ? '#ecfdf5'
                           : analisis.confianza >= 0.5 ? '#fef3c7'
                           : '#fef2f2',
                    color:   analisis.confianza >= 0.85 ? '#166534'
                           : analisis.confianza >= 0.5 ? '#92400e'
                           : '#991b1b',
                    fontWeight: 700, fontSize: 11,
                  }} />
              )}
              {analisis.razon && (
                <Typography sx={{ fontSize: 10.5, color: '#94a3b8', fontStyle: 'italic', width: '100%', mt: 0.25 }}>
                  {analisis.razon}
                </Typography>
              )}
            </Box>
          )}

          {/* Sugerencia de match ambigua o sin encontrar */}
          {analisis?.cuenta_destino_match?.candidatos?.length > 0
            && !cuentaSugerida && (
              <Box sx={{ mt: 0.75 }}>
                <Typography sx={{ fontSize: 10.5, color: '#64748b', mb: 0.25 }}>
                  Cuentas parecidas:
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {analisis.cuenta_destino_match.candidatos.slice(0, 3).map(c => (
                    <Chip key={c.id} size="small" label={`${c.nombre} (${c.score})`}
                      sx={{ bgcolor: '#f1f5f9', fontSize: 10.5, height: 20 }} />
                  ))}
                </Box>
              </Box>
            )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1, fontSize: 11 }}>{error}</Alert>
      )}

      {discrepancia && (
        <Alert severity="warning" sx={{ mt: 1, fontSize: 11 }}>
          El comprobante muestra <b>{fmtMoney(discrepancia.detectado)}</b>, pero
          el pago suma <b>{fmtMoney(discrepancia.distribuido)}</b>
          {' '}(diferencia de {discrepancia.pct.toFixed(1)}%). Revisa antes de guardar.
        </Alert>
      )}
    </Box>
  );
}


/**
 * useComprobanteUploader — Hook helper que encapsula el flujo upload + análisis.
 * Reutilizado por AplicarPagoDialog (CxC) y por los diálogos de liquidación
 * de comisiones. Retorna el estado y los handlers listos para el componente
 * `ComprobanteBlock`.
 *
 * Params:
 *   apiBase           Base de la API (ej. process.env.REACT_APP_API_URL)
 *   getAuthHeaders    Función que retorna los headers auth
 *   empresaId         String del empresa_id
 *   getMontoEsperado  Función () => number con el monto que Vision comparará
 *
 * Return:
 *   { url, nombre, subiendo, analizando, analisis, error,
 *     handleFile, quitar, reset }
 */
export function useComprobanteUploader({ apiBase, getAuthHeaders, empresaId, getMontoEsperado }) {
  const [url, setUrl] = useState('');
  const [nombre, setNombre] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [analizando, setAnalizando] = useState(false);
  const [analisis, setAnalisis] = useState(null);
  const [error, setError] = useState('');

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setAnalisis(null);
    setSubiendo(true);
    try {
      // 1) Upload
      const fd = new FormData();
      fd.append('archivo', file);
      const upToken = localStorage.getItem('token');
      const upHeaders = upToken ? { Authorization: `Bearer ${upToken}` } : {};
      const upRes = await fetch(`${apiBase}/turismo/soportes/upload`, {
        method: 'POST', body: fd, headers: upHeaders,
      });
      const upJson = await upRes.json().catch(() => ({}));
      if (!upRes.ok) {
        setError(upJson?.detail || `Error subiendo archivo (${upRes.status})`);
        setSubiendo(false);
        return null;
      }
      setUrl(upJson.url);
      setNombre(file.name);
      setSubiendo(false);

      // 2) Análisis con Vision (solo imágenes — PDF no soportado)
      const esPDF = /\.pdf$/i.test(file.name);
      if (esPDF) {
        setAnalisis({
          ok: false,
          razon: 'PDF subido. Vision solo soporta imágenes — completa los campos manualmente.',
        });
        return { url: upJson.url, analisis: null };
      }
      setAnalizando(true);
      const analRes = await fetch(`${apiBase}/turismo/soportes/analizar-comprobante`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({
          url: upJson.url,
          monto_esperado: getMontoEsperado ? getMontoEsperado() : 0,
          empresa_id: empresaId,
        }),
      });
      const analJson = await analRes.json().catch(() => ({}));
      setAnalizando(false);
      if (!analRes.ok) {
        setError(analJson?.detail || `Error analizando comprobante (${analRes.status})`);
        return { url: upJson.url, analisis: null };
      }
      setAnalisis(analJson);
      return { url: upJson.url, analisis: analJson };
    } catch (e) {
      setError(`Error de red: ${e.message}`);
      setSubiendo(false);
      setAnalizando(false);
      return null;
    }
  };

  const quitar = () => {
    setUrl('');
    setNombre('');
    setAnalisis(null);
    setError('');
  };

  const reset = quitar;

  return { url, nombre, subiendo, analizando, analisis, error, handleFile, quitar, reset };
}
